import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input, Button, Modal, Row, Col } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { FC } from "react";
import { SocketTopics } from "../../constants/enums";

const { TextArea } = Input;

let interval;

/*
  The simulator and the sensor message it adds have to name the same device, or
  the reader subscribes to one topic while the samples are published to another
  and sits on "waiting for data" forever — which is what happened while the
  simulation published to the connection's iot-service append.

  Publishing needs a concrete topic: the trailing `/#` is a subscribe wildcard
  and cannot be published to, so the leaf is spelled out separately and the
  wildcard subscription still matches it.
*/
const SIM_UDID = "feaa3a96-6c78-4e70-a215-a4def3a7d57a";
const SIM_USER_ID = "103706";
const SIM_IOT_USER_ID = "45";
const SIM_TOPIC_ROOT = `beamdevlive/device/${SIM_UDID}/user/${SIM_USER_ID}/iotAppend/${SIM_IOT_USER_ID}`;
const SIM_SUBSCRIBE_TOPIC = `${SIM_TOPIC_ROOT}/#`;
const SIM_PUBLISH_TOPIC = `${SIM_TOPIC_ROOT}/rx`;

const GPSModal: FC<{ onClose?: () => void; socket: ISocket }> = ({ onClose, socket }) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(true);
  const [key, setKey] = useState<string>("");
  const [simulation, setSimulation] = useState<boolean>(false);
  
  const getRandomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
  
  const getRandomFloat = (min, max) => {
    return parseFloat((Math.random() * (max - min) + min).toFixed(1));
  }

  const [simulationData, setSimulationData] = useState({
    heartRate: getRandomInt(60, 90),
    bodyTemp: getRandomFloat(36.1, 37.9),
    bloodOxygen: getRandomInt(95, 100),
    bloodPressureHigh: getRandomInt(110, 130),
    bloodPressureLow: getRandomInt(60, 70),
    cal: 0,
    steps: 0,
  });
  
  const handleCancel = () => {
    setIsModalVisible(false);
    onClose && onClose();
  };

  const startSimulation = () => {
    setSimulation(true);
    interval = setInterval(() => {
      setSimulationData((prevData) => ({
        heartRate: getRandomInt(60, 90),
        bodyTemp: getRandomFloat(36.1, 37.9),
        bloodOxygen: getRandomInt(95, 100),
        bloodPressureHigh: getRandomInt(110, 130),
        bloodPressureLow: getRandomInt(60, 70),
        cal: prevData.cal + getRandomInt(1, 4), 
        steps: prevData.steps + getRandomInt(5, 10),
      }));
    }, 10000);
  }

  const stopSimulation = () => {
    setSimulation(false);
    interval && clearInterval(interval);
  }

  const dispatch = useDispatch();

  useEffect(()=>{
    return () => interval && clearInterval(interval);
  }, [])

  useEffect(()=>{
    if(simulation){
      console.log('simulationData', {
        "samples": [
          {
            "timestamp":new Date().getTime(),
            "dataset": simulationData
          }
        ],
        "iotConnectionId": "108816"
      });
      const sample = {
        "samples": [
          {
            "timestamp":new Date().getTime(),
            "dataset": simulationData
          }
        ],
        "iotConnectionId": "108816"
      };

      // The connection append is what the backend pipeline consumes.
      socket.publishToEvent(
        SocketTopics.PublishPageTopics,
        "beamdevlive/connection/108816/service/iot-service/append",
        sample
      );

      // The device append is what anything watching this sensor on a page is
      // subscribed to, and it is the only one routed to handleIotAppend — the
      // socket dispatcher keys on the topic containing "iotAppend".
      socket.publishToEvent(
        SocketTopics.PublishPageTopics,
        SIM_PUBLISH_TOPIC,
        sample
      );

      /*
        The publish above does not come back. The broker reserves the device
        topics for real devices, and it refuses a web client's publish the way
        MQTT refuses anything — silently, with the PUBACK still sent — so there
        is no error to react to and nothing to fix from this side.

        The sample is therefore also handed straight to the store, exactly as
        handleIotAppend would have on delivery. This is the simulator, not a
        device: its whole purpose is to produce readings without one, and a
        simulate button that depends on a broker round trip it is not permitted
        to make would simply never work. Real devices are unaffected — they
        publish from the device side, where the topic is theirs to write to.
      */
      dispatch({
        type: "ADD_RX",
        payload: { ...sample.samples[0], imei: SIM_PUBLISH_TOPIC },
      });
    }
  }, [simulationData, simulation])

  const handleOk = () => {
    if (key !== undefined && key !== "") {
      dispatch({
        type: "SEND_MESSAGE",
        payload: {
          message: key,
          type: "rx",
          data: {
            "body": SIM_UDID,
            "iotUdid": SIM_UDID,
            "iotUserId": SIM_IOT_USER_ID,
            "startIotAt": new Date().getTime(),
            "userId": SIM_USER_ID,
            "iotServiceId": "iot",
            "shareIotMessageId": "988419",
            "iotTopic": SIM_SUBSCRIBE_TOPIC,
            "allChannels": `ecosystem=beamdevlive iotUdid=${SIM_UDID} userId=${SIM_USER_ID} iotUserId=${SIM_IOT_USER_ID} page=true`,
            "iotType": "rx",
          }
        },
      });
    }
    setIsModalVisible(false);
  };

  return (
    <Modal
      title="RX Sensor"
      visible={isModalVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      width="480px"
      className="beam-modal"
      footer={[]}
      destroyOnClose={true}
    >
      <Row>
        <Col span={24}>
          <Input
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
            }}
            placeholder="Device key"
          />
        </Col>
      </Row>
      <Row className="action-grp mt-4">
      <Col span={8} className="pr-2">
          <Button
            className="beam-btn yellow-btn bordered w-full"
            onClick={()=>{
              simulation ? stopSimulation(): startSimulation()
            }}
          >
            {simulation ? "Stop": "Simulate"}
          </Button>
        </Col>
        <Col span={8} className="pr-2">
          <Button
            className="beam-btn green-btn cover w-full"
            onClick={handleOk}
          >
            Add
          </Button>
        </Col>
        <Col span={8}>
          <Button
            className="beam-btn orange-btn bordered w-full"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default GPSModal;
